import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Picture from '@/image/hotel/monthly_plan.png';
import { MenuBar } from '@/components/common/MenuBar';
import { News } from '@/components/hotel/News';
import { PropertyCard } from '@/components/monthly/properties';
import { MagazineParts } from '@/components/hotel/MagazineParts';
import { ApartmentContent } from '@/components/monthly/apartment';
import { VerticalTextImage } from '@/components/common/VerticalTextImage';
import { FeatureImageSlider } from '@/components/monthly/FeatureImageSlider';
import { FooterItems } from '@/components/common/FooterItems';
import { getPropertiesAll } from '@/API/apiService';

interface APIProperty {
  property_id: number;
  property_name: string;
  property_name_en: string;
  property_description: string;
  property_description_en: string;
  room_type_description: string;
  room_type_description_en: string;
  station_distance: string;
  station_distance_en: string;
  area_id: number;
  area: {
    id: number;
    name: string;
    name_en: string;
  };
  sub_areas: Array<{
    id: number;
    name: string;
    name_en: string;
  }>;
  chillnn_id: string;
  images: Array<{
    image_url: string;
    caption: string;
    caption_en: string;
  }>;
  availability_period: {
    start: string;
    end: string;
  } | null;
  room_type_lowest_price: string;
  room_area: string; // 新規追加：例 "37.30"
}

interface MappedProperty {
  property_id: number;
  images: string[];
  title: string;
  description: string;
  availability: string;
  area: string;
  subarea: string;
  rent: string;
  roomDetails: string;
  area_id: string;
  station_distance: string;
  room_area: string; // 新規追加：room_area の値
}

export const MonthlyPage: React.FC = () => {
  // URLから言語判定
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  // Meta / OGP タグの内容を設定
  const metaTitle = useEnglish
    ? 'Apartments for Rent Tokyo | The Apartment Hotel by stylio'
    : 'The Apartment Hotel by stylio｜東京のネイバーフッドを感じる、家具付き賃貸住宅';

  const metaDescription = useEnglish
    ? 'The Apartment Hotel is furnished apartments for monthly rent, located in the areas where you can find local institutions co-existing with independent, creative restaurants, cafés and bars.'
    : '入退去はオンラインで完結・最短1ヶ月から、家具家電付きのお部屋を用意。\n\nネイバーフッドが豊かな暮らしを。\n都心へのアクセス性と魅力的な生活圏を兼ねそろえる賃貸住宅ブランドです。';

  const ogpTitle = useEnglish
    ? 'Apartments for Rent Tokyo | The Apartment Hotel'
    : 'The Apartment Hotel -  東京のネイバーフッドを感じる、家具付き賃貸住宅';

  const ogpDescription = useEnglish
    ? "Apply online for the furnished rooms, available with minimum one month contract.\n\nWe propose a hotel stay woven into Tokyo's neigborhoods.\nThe apartment hotel brand combines easy accessibility from central Tokyo, while offering a local stay in the cool neighboorhoods."
    : metaDescription; // 日本語版は metaDescription と同じ

  const [properties, setProperties] = useState<MappedProperty[]>([]);

  useEffect(() => {
    getPropertiesAll()
      .then((response) => {
        const apiData: APIProperty[] = response.data.data;
        const mapped = apiData.map((prop) => {
          // 画像配列はそのまま。画像がない場合は Picture を複数回利用
          const imageUrls = prop.images?.length
            ? prop.images.map((img) => img.image_url)
            : [Picture, Picture, Picture].map((p) => p.toString());

          // 空室情報：言語に合わせて切り替え
          const availabilityStr = prop.availability_period
            ? useEnglish
              ? `(${prop.availability_period.start} ~ Vacancy)`
              : `(${prop.availability_period.start} ~ 空室有)`
            : useEnglish
              ? '(No vacancy info)'
              : '(空室情報なし)';

          // 家賃表示：英語の場合は「/month」を付与
          const rentStr = prop.room_type_lowest_price
            ? useEnglish
              ? `¥${parseInt(prop.room_type_lowest_price, 10).toLocaleString()}〜/month`
              : `¥${parseInt(prop.room_type_lowest_price, 10).toLocaleString()}〜/月`
            : '-';

          const title = useEnglish ? prop.property_name_en : prop.property_name;
          const description = useEnglish
            ? prop.property_description_en
            : prop.property_description;
          const area = prop.area
            ? useEnglish
              ? prop.area.name_en
              : prop.area.name
            : '';

          // subarea を API の sub_areas の先頭要素から取得
          const subarea =
            prop.sub_areas && prop.sub_areas.length > 0
              ? useEnglish
                ? prop.sub_areas[0].name_en
                : prop.sub_areas[0].name
              : '';

          const roomDetails = useEnglish
            ? prop.room_type_description_en
            : prop.room_type_description;
          const stationDistance = useEnglish
            ? prop.station_distance_en
            : prop.station_distance;

          return {
            property_id: prop.property_id,
            images: imageUrls,
            title,
            description,
            availability: availabilityStr,
            area,
            subarea,
            rent: rentStr,
            roomDetails,
            area_id: prop.area_id.toString(),
            station_distance: stationDistance,
            room_area: prop.room_area, // API の room_area の値
          };
        });
        setProperties(mapped);
      })
      .catch((err) => {
        console.error('Failed to fetch properties data:', err);
      });
  }, [useEnglish]);

  // FeatureImageSlider 用のデータ（言語による切替）
  const featureImages = useEnglish
    ? [
        {
          src: Picture,
          title: 'Sufficient Facilities',
          text: 'Furniture, appliances, kitchenware, water, gas, electricity, WiFi, everything you need is provided.',
        },
        {
          src: Picture,
          title: 'Smart Contract',
          text: 'Everything is completed online. Fast and smart contract that allows you to live in the apartment from the very first day.',
        },
        {
          src: Picture,
          title: 'Flexible Terms',
          text: 'Available from one month minimum. Flexible lifestyle as you are free to move out in one-month increments.',
        },
      ]
    : [
        {
          src: Picture,
          title: '生活に必要なもの完備',
          text: '家具、家電、キッチン用品、水道、ガス、電気、WiFi。生活に必要なものは、すべて備え付け。',
        },
        {
          src: Picture,
          title: 'スマートな契約',
          text: 'すべてオンラインで完結。最速申込みから一週間後にその日から入居可能な、スピーディーでスマートな契約。',
        },
        {
          src: Picture,
          title: 'フレキシブルな期間',
          text: '最低一ヶ月から利用可能。一ヶ月以降、自由に退去可能なので、柔軟なライフスタイルに対応。',
        },
      ];

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={ogpTitle} />
        <meta property="og:description" content={ogpDescription} />
        <meta
          property="og:image"
          content="https://example.com/path-to-og-image.jpg"
        />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      <MenuBar
        leftText="Hotel Site"
        rightText="Rent Room"
        leftLink="/hotel"
        rightLink="#rooms"
        isFooterVisible
      >
        <div className="bg-whiteYellow relative w-full max-md:pb-40">
          <VerticalTextImage
            imageSrc={Picture}
            verticalText="Monthly"
            imageTall={true}
            bottomText={
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    "Apartment Hotel<span className='block sm:inline'> For Neighborhood Stay</span>",
                }}
              />
            }
          />
          <div className="px-4">
            <ApartmentContent />
            <div
              className="max-md:py-4 pb-[60px] text-center text-lg"
              id="rooms"
            >
              ( LOCATIONS )
            </div>
            <div className="flex justify-center" id="rooms">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:max-w-[600px]">
                {properties.map((property, index) => (
                  <PropertyCard key={index} property={property} />
                ))}
              </div>
            </div>
            <div className="mb-[150px] mt-32">
              <FeatureImageSlider images={featureImages} />
            </div>
            <div className="mb-[6.25rem]">
              <MagazineParts />
            </div>
            <div className="mb-[6.25rem]">
              <News />
            </div>
            <FooterItems />
          </div>
        </div>
      </MenuBar>
    </>
  );
};

export default MonthlyPage;
